import React, { useState } from "react";
import clsx from "clsx";
import { ImageUploadIcon, SelectedStarIcon, UnselectedStarIcon } from "../../icons";

import { P_Props } from "./ImageUpload.type";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { DEFAULT_IMG_ALT } from "../../../constants";
import { DeleteOutline as DeleteOutlineIcon, VisibilityOutlined as VisibilityOutlinedIcon } from "@material-ui/icons";
import { Dialog, TextField } from "@material-ui/core";
import { SimpleModalHeader } from "..";
import { loadCallback } from "../../../utils";

type ImagePreview = {
  visible: boolean;
  initData: { id?: string; src?: string; alt: string; file: any };
};

const ImageUploadPresenter: React.FC<P_Props> = (props) => {
  const classes = clsx({ "image-upload": true, [props.className || ""]: Boolean(props.className) });
  const [imagePreview, setImagePreview] = useState<ImagePreview>({
    visible: false,
    initData: {
      file: undefined,
      alt: "",
    },
  });

  const changeImagePreviewState = <T extends keyof ImagePreview>(key: T, value: ImagePreview[T]) => {
    setImagePreview({
      ...imagePreview,
      [key]: value,
    });
  };

  const closeImagePreviewModal = () => {
    setImagePreview({ visible: false, initData: { alt: "", file: undefined } });
  };

  const handleUpload = (event) => {
    const file: File = event.target.files[0];
    const reader = new FileReader();
    reader.onloadend = function (e) {
      props.onChange({ file, fileStr: reader.result });
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handleModalUpload = (event) => {
    const file: File = event.target.files[0];
    const reader = new FileReader();
    reader.onloadend = function (e) {
      changeImagePreviewState("initData", { ...imagePreview.initData, file, src: reader.result as string });
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handelModalSave = () => {
    props.onChange({
      id: imagePreview.initData.id,
      file: imagePreview.initData.file,
      fileStr: imagePreview.initData.src,
      alt: imagePreview.initData.alt,
    });
    setImagePreview({ visible: false, initData: { alt: "", file: undefined } });
  };

  return (
    <>
      <div className={classes} style={props.style}>
        {props.fileList.length < props.max && !Boolean(props.config?.uploadModal) && (
          <div className="image-upload__input">
            <label>
              <ImageUploadIcon size={[32, 32]} color="#6658F3" />
            </label>
            <input accept="image/*" type="file" onChange={handleUpload} />
          </div>
        )}

        {props.fileList.length < props.max && Boolean(props.config?.uploadModal) && (
          <button
            className="image-upload__upload-btn"
            onClick={(e) => {
              e.preventDefault();
              changeImagePreviewState("visible", true);
            }}
          >
            <ImageUploadIcon size={[32, 32]} color="#6658F3" />
          </button>
        )}

        {props.fileList.map((file) => {
          // console.log(file);
          let isSelected: boolean = false;

          if (props.selectConfig?.selectedIds.length) {
            isSelected = props.selectConfig.selectedIds.includes(file.id);
          }

          return (
            <div key={file.id} className="image-upload__img">
              <LazyLoadImage alt={DEFAULT_IMG_ALT} src={file.src} />
              <div className="image-upload__img__actions">
                {Boolean(props.config?.uploadModal) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setImagePreview({ visible: true, initData: { ...file, alt: file.alt || "", file: undefined } });
                    }}
                  >
                    <VisibilityOutlinedIcon style={{ fontSize: "2rem", color: "#fff" }} />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    props.onDelete(file.id);
                  }}
                >
                  <DeleteOutlineIcon style={{ fontSize: "2rem", color: "#fff" }} />
                </button>
              </div>

              {props.selectConfig && (
                <>
                  {isSelected ? (
                    <button
                      className="image-upload__img__selected"
                      onClick={(e) => {
                        e.preventDefault();
                        loadCallback(props.selectConfig?.onSelect, file.id, file);
                      }}
                    >
                      <SelectedStarIcon size={[19, 19]} color="#ffbc1f" />
                    </button>
                  ) : (
                    <button
                      className="image-upload__img__unselected"
                      onClick={(e) => {
                        e.preventDefault();
                        loadCallback(props.selectConfig?.onSelect, file.id, file);
                      }}
                    >
                      <UnselectedStarIcon size={[19, 19]} color="#bdc6d7" />
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <Dialog open={imagePreview.visible} maxWidth="sm" fullWidth>
        <div className="image-upload__preview">
          <SimpleModalHeader
            title="Upload Image"
            onClose={() => {
              closeImagePreviewModal();
            }}
          />
          <div className="image-upload__preview__body">
            <div className="image-upload__preview__body__upload">
              {imagePreview.initData.src ? (
                <div className="image-upload__preview__body__upload__img">
                  <LazyLoadImage alt={DEFAULT_IMG_ALT} src={imagePreview.initData.src} />
                  <div className="image-upload__preview__body__upload__img__actions">
                    <button
                      onClick={() => {
                        changeImagePreviewState("initData", {
                          ...imagePreview.initData,
                          src: undefined,
                          file: undefined,
                        });
                      }}
                    >
                      <DeleteOutlineIcon style={{ fontSize: "2rem", color: "#fff" }} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="image-upload__preview__body__input">
                  <label>
                    <ImageUploadIcon size={[32, 32]} color="#6658F3" />
                  </label>
                  <input accept="image/*" type="file" onChange={handleModalUpload} />
                </div>
              )}
            </div>
            <div className="image-upload__preview__body__alt">Alternate text for an image (ALT)</div>
          </div>
          <div className="image-upload__preview__actions">
            <TextField
              fullWidth
              placeholder="Alt"
              variant="outlined"
              className="form-text-field"
              inputProps={{ className: "input" }}
              value={imagePreview.initData.alt}
              onChange={(e) => {
                const value = e.target.value;

                changeImagePreviewState("initData", {
                  ...imagePreview.initData,
                  alt: value,
                });
              }}
            />
            <button
              disabled={Boolean(
                (!imagePreview.initData.file && !imagePreview.initData.id) || 
                (imagePreview.initData.id && !imagePreview.initData.src)
              )}
              className="image-upload__preview__actions__save"
              onClick={() => {
                handelModalSave();
              }}
            >
              Save
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ImageUploadPresenter;
